'use client';

import { Loader2, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

type Props = { src?: string; poster?: string; className?: string; mediaClassName?: string };

/** Upload confirmation precedes poster processing. The video itself is previewable now. */
export function KagemaneVideoPreview(props: Props) {
  return <VideoPreview key={`${props.src ?? ''}\n${props.poster ?? ''}`} {...props} />;
}

function VideoPreview({ src, poster, className = '', mediaClassName = '' }: Props) {
  const t = useTranslations('kagemane');
  const container = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  // Old duplicate-upload responses incorrectly used the MP4 as its own poster.
  const [usePoster, setUsePoster] = useState(Boolean(poster && poster !== src));
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    if (!container.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '160px' });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || state !== 'loading') return;
    const timer = window.setTimeout(() => {
      if (usePoster && src) setUsePoster(false);
      else setState('error');
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [visible, state, usePoster, src]);

  return <span ref={container} className={`relative grid place-items-center overflow-hidden bg-[#E9EDF0] ${className}`} aria-busy={state === 'loading'}>
    {visible && (usePoster ? <img src={poster} alt="" className={`${mediaClassName} ${state === 'ready' ? '' : 'opacity-0'}`}
      onLoad={() => setState('ready')} onError={() => { setUsePoster(false); setState('loading'); }} />
      : src ? <video src={src} muted playsInline preload="metadata" aria-hidden tabIndex={-1}
        className={`pointer-events-none ${mediaClassName} ${state === 'ready' ? '' : 'opacity-0'}`}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          if (Number.isFinite(video.duration) && video.duration > 0) video.currentTime = Math.min(0.1, video.duration / 2);
        }}
        onLoadedData={() => setState('ready')} onSeeked={() => setState('ready')}
        onError={() => setState('error')} /> : null)}
    {state !== 'ready' && <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-[#667680]" role="status">
      {state === 'loading' && (src || usePoster)
        ? <><Loader2 className="size-5 animate-spin motion-reduce:animate-none" aria-hidden /><span className="text-xs">{t('videoPreviewLoading')}</span></>
        : <><Video className="size-6" aria-hidden /><span className="text-xs">{t('videoPreviewUnavailable')}</span></>}
    </span>}
  </span>;
}
